import { Paper, Stack, Typography } from '@mui/material'
import { ExecutedMovesTimeRangeDto } from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import { TomTable, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { VesselVisitKPIUIStore } from '../../stores/vessel-visit-kpi.ui-store'
import { TableCentralizedCell } from '../atoms/TableCentralizedCell'
import { CraneTableContent } from '../molecules/CraneTableContent'
import { CraneTableFooter } from '../molecules/CraneTableFooter'
import { TotalTableContent } from '../molecules/TotalTableContent'
import { TotalTableFooter } from '../molecules/TotalTableFooter'
import { VesselTableHeader } from '../molecules/VesselTableHeader'

interface Props {
  store: VesselVisitKPIUIStore
  enablePagination?: boolean
}
export const VesselVisitKPITable = observer(({ store, enablePagination = true }: Props) => {
  const { t } = useTranslate()
  const [columnsOrder, setColumnsOrder] = useState<string[]>([])

  useEffect(() => {
    setColumnsOrder([
      'date',
      ...store.craneColumns.map(column => column.key),
      'totals',
      'grandTotal',
    ])
  }, [store.craneColumns])

  const columns = computed(() => {
    const columns: MRT_ColumnDef<ExecutedMovesTimeRangeDto>[] = [
      {
        accessorKey: 'date',
        header: t('time', 'Time'),
        enableColumnActions: false,
        Header: () => <Typography variant='subtitle1'>{t('time', 'Time')}</Typography>,
        sortingFn: (rowA, rowB) => dayjs(rowA.original.date).diff(rowB.original.date),
        Footer: !store.executedMoves.length
          ? undefined
          : () => <Typography variant='subtitle1'>{t('totals', 'Totals')}</Typography>,
        Cell: cell => (
          <Stack>
            <Typography variant='subtitle2'>
              {new Date(cell.row.original.date).toLocaleTimeString([], {
                timeStyle: 'short',
                hour12: false,
              })}
              {' - '}
              {dayjs(cell.row.original.date).add(1, 'hours').toDate().toLocaleTimeString([], {
                timeStyle: 'short',
                hour12: false,
              })}
            </Typography>
            <Typography variant='body2'>{dayjs(cell.row.original.date).format('l')}</Typography>
          </Stack>
        ),
      },
      ...store.craneColumns.map(column => ({
        accessorKey: column.key,
        enableColumnActions: false,
        enableSorting: false,
        size: 120,
        header: t(column.label, column.label),
        Header: () => <VesselTableHeader column={column} />,
        Footer: !store.executedMoves.length
          ? undefined
          : () => {
              const craneTotal = store.cranesTotalFooter.find(
                total => total.craneId === column.craneId,
              )
              return <CraneTableFooter column={column} craneTotal={craneTotal} />
            },
        Cell: (cell: { row: MRT_Row<ExecutedMovesTimeRangeDto> }) => {
          const craneMove = cell.row.original.executedMoves.find(
            move => move.equipmentId === column.craneId,
          )
          return <CraneTableContent column={column} craneMove={craneMove} />
        },
      })),
      {
        accessorKey: 'totals',
        header: t('total', 'Total'),
        enableColumnActions: false,
        enableSorting: false,
        size: 100,
        Header: () => <VesselTableHeader column={store.totalColumn} skipTotal />,
        Footer: !store.executedMoves.length
          ? undefined
          : () => <TotalTableFooter column={store.totalColumn} total={store.totalFooter} />,
        Cell: cell => <TotalTableContent column={store.totalColumn} moves={cell.row.original} />,
      },
      {
        accessorKey: 'grandTotal',
        header: t('grandTotal', 'Grand total'),
        Header: () => <TableCentralizedCell>{t('grandTotal', 'Grand total')}</TableCentralizedCell>,
        enableSorting: false,
        enableColumnActions: false,
        Footer: !store.executedMoves.length
          ? undefined
          : () => (
              <TableCentralizedCell dataCy='grand-total-footer'>
                {store.totalFooter.total}
              </TableCentralizedCell>
            ),
        Cell: cell => <TableCentralizedCell>{cell.row.original.total}</TableCentralizedCell>,
      },
    ]

    return columns
  }).get()

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'operations/vessel-visit-kpis',
  )

  const table = useTomTable<ExecutedMovesTimeRangeDto>(
    {
      data: store.executedMoves,
      columns: columns,
      state: { columnOrder: columnsOrder },
      enableRowSelection: false,
      enableExpanding: false,
      enableColumnFilters: false,
      enableRowActions: false,
      initialState: {
        columnPinning: {},
      },
      enableTopToolbar: false,
      enablePagination: enablePagination,
      muiTableContainerProps: { sx: { height: enablePagination ? 'auto' : 'fit-content' } },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <Paper variant='elevation' elevation={1}>
      <TomTable
        table={table}
        keepPaginationState={false}
        keepColumnFiltersState={false}
        keepColumnSortingState={false}
      />
    </Paper>
  )
})
